<template>
  <div class="release-packages-table">
    <p>{{ $t('APP.RELEASES.PACKAGES') }}</p>

    <ElTable
      :show-header="true"
      :data="release.packages"
      row-key="id"
      class="release-packages-table__table"
      cell-class-name="release-packages-table__cell"
    >
      <ElTableColumn width="200" :label="$t('APP.RELEASES.VERSION')">
        <template slot-scope="{ row: { versionCode, versionName } }">
          <span class="release-packages-table__version-cell">
            {{ `${versionCode} (${versionName})` }}
          </span>
        </template>
      </ElTableColumn>

      <ElTableColumn
        width="150"
        :label="$t('APP.RELEASES.PACKAGE_UPLOAD_DATE')"
      >
        <template slot-scope="{ row: { uploadDate } }">
          {{ translateDate(uploadDate, lang) }}
        </template>
      </ElTableColumn>

      <ElTableColumn width="90" :label="$t('APP.RELEASES.STATE')">
        <template slot-scope="{ row: { publishState } }">
          <span :class="stateCellClass">
            {{ getPackageState(publishState) }}
          </span>
        </template>
      </ElTableColumn>

      <ElTableColumn width="90" :label="$t('APP.RELEASES.MIN_SDK')">
        <template slot-scope="{ row: { apiLevel } }">
          <span class="text-latin">{{ apiLevel }}</span>
        </template>
      </ElTableColumn>

      <ElTableColumn
        v-slot="{ row: currentPackage }"
        width="180"
        :label="$t('APP.RELEASES.CPU_ARCHITECTURE')"
      >
        <ReleasePackageArchitecture
          :key="currentPackage.token"
          :package-item="currentPackage"
          :can-edit="canChangeCpu(currentPackage)"
        />
      </ElTableColumn>

      <ElTableColumn min-width="250">
        <div
          slot-scope="{ row: currentPackage }"
          class="release-packages-table__actions"
        >
          <MoreOptionsButton
            :package-item="currentPackage"
            :release="release"
          />
        </div>
      </ElTableColumn>

      <template slot="empty">
        {{ $t('APP.RELEASES.NO_PACKAGES') }}
      </template>
    </ElTable>

    <AddPackageToRelease
      v-if="canEditPackages"
      :release-id="release.token"
      @add-package="addPackageToLocalRelease"
    />
  </div>
</template>

<script>
import appState from '@/mixins/appState';
import packageName from '@/mixins/packageName';
import lang from '@/mixins/lang';
import { translateDate } from '@/utils/date';
import { mapActions, mapMutations } from 'vuex';
import { ADD_PACKAGE_TO_RELEASE, RELEASES } from '@/store/types';
import AddPackageToRelease from './AddPackageToRelease.vue';
import ReleasePackageArchitecture from '@@/app/AppReleases/common/ReleasePackageArchitecture';
import MoreOptionsButton from './MoreOptionsButton';

export default {
  name: 'ReleasePackagesTable',
  components: {
    AddPackageToRelease,
    ReleasePackageArchitecture,
    MoreOptionsButton,
  },
  mixins: [appState, lang, packageName],
  props: {
    release: {
      type: Object,
      required: true,
    },
  },
  computed: {
    canEditPackages() {
      return this.release.actions.editPackage;
    },
    stateCellClass() {
      return { 'text-line-through': this.release.publishState === 'inactive' };
    },
  },
  methods: {
    ...mapMutations({ addPackageToRelease: ADD_PACKAGE_TO_RELEASE }),
    ...mapActions({ fetchReleases: RELEASES }),

    translateDate,

    canChangeCpu(packageItem) {
      const { publishState: releaseState, isLastRelease } = this.release;
      const packageIsNotRemoved = packageItem?.publishState !== 'S';
      const isEditingRelease = releaseState === 'editing';
      return Boolean(isLastRelease && isEditingRelease && packageIsNotRemoved);
    },

    addPackageToLocalRelease(packageItem) {
      this.addPackageToRelease({
        releaseId: this.release.token,
        packageItem,
      });

      this.fetchReleases(this.packageName);
    },
  },
};
</script>

<style lang="scss">
.release-packages-table {
  background-color: $lb-lighter-gray !important;
  border-top: 1px solid $lb-light-gray;
  border-bottom: 1px solid $lb-light-gray;
  padding: $global-padding;

  &__actions {
    display: flex;
    flex-direction: row-reverse;
  }

  &__button {
    margin-left: calc($global-margin / 2) !important;

    @include rtl() {
      margin-left: 0 !important;
      margin-right: calc($global-margin / 2);
    }
  }

  &__table {
    border: $--border-base;

    .cell {
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
